/*  MUI  */
import { Box, Skeleton, Typography } from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { BigCalendar } from 'application/Shared/Components/BigCalendar';

/*  NPM  */
import { addMonths, isLastDayOfMonth, lastDayOfMonth } from 'date-fns';

/*  Objects  */
import { Application } from 'objects/Application';
import { Contract, ContractTypeIcon } from 'objects/Contract';

/*  React  */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContractDetailsModal } from './internal/AppContractDetailsModal';
import { ContractListForSpecificDayModal } from './internal/ContractListForSpecificDayModal';

const eventTitle = (row) => (
    <Box display="flex" alignItems="center" data-testid="eventTitle">
        <Contract data={row}>
            <ContractTypeIcon
                fontSize="small"
                sx={{
                    color:
                        row.type === 'month'
                            ? 'bigCalendar.monthly.icon'
                            : 'bigCalendar.renew.icon',
                    marginRight: '3px'
                }}
            />
        </Contract>
        <Application data={row.application}>
            <ApplicationNameLinkCell nameNoTruncate />
        </Application>
    </Box>
);

const getMonthlyDate = ({ dateInTheStartingCalendarMonth, day }) => {
    const calendarsCurrentYear = dateInTheStartingCalendarMonth.getFullYear();
    const calendarsCurrentMonth = dateInTheStartingCalendarMonth.getMonth();
    if (
        isLastDayOfMonth(day) ||
        (calendarsCurrentMonth === 1 && [29, 30, 31].includes(day.getDate()))
    ) {
        // If the monthly renew date is at the end of that month.
        // Or if the month is February and the renewal date is on the 29th, 30th, or 31st.
        // We add this logic here so that an Oct. 31 renew date does not fall on Feb. 31.
        return new Date(
            calendarsCurrentYear,
            calendarsCurrentMonth,
            lastDayOfMonth(dateInTheStartingCalendarMonth).getDate()
        );
    }
    return new Date(calendarsCurrentYear, calendarsCurrentMonth, day.getDate());
};

const getEvents = ({ allEvents, forQuarter, range }) => {
    // getEvents will get called when we updateEvents. updateEvents's range will be acquired via the date prop that we send
    // to the BigCalendar (single date), or automatically from BigCalendar's onRangeChange. onRangeChange returns an object, {start: date, end: date}.
    // If no date prop is provided, then the initial updateEvents' range will be undefined until the user navigates from month to month.
    let dateInTheStartingCalendarMonth = new Date();
    if (range) {
        if (range instanceof Date) {
            // It is a single date, not an object/range
            dateInTheStartingCalendarMonth = range;
        } else {
            // It is not a single date, it is an object. We need to convert to a single date.
            // 7 days is added because range can start with days from the previous month that are not displayed
            // because not all months start on a Sunday, e.g., for the calendar month of August 2023,
            // range = {start: Sun Jul 30 2023 00:00:00 GMT-0400 (Eastern Daylight Time), end: Sat Sep 02 2023 23:59:59 GMT-0400 (Eastern Daylight Time)}
            dateInTheStartingCalendarMonth = new Date(
                new Date(range.start).getTime() + 1000 * 60 * 60 * 24 * 7 // 7 days
            );
        }
    }

    const eventsMap = allEvents.reduce(
        (acc, event) => {
            const day = new Date(event.renewalDate);
            if (event.type === 'month') {
                // monthly type, so get the day and set an event for the current month and year
                const monthlyDate = getMonthlyDate({
                    dateInTheStartingCalendarMonth,
                    day
                });

                acc.firstMonthDate.push({
                    allDay: true,
                    appName: event.application.name,
                    end: monthlyDate,
                    resource: event,
                    start: monthlyDate,
                    title: eventTitle(event)
                });
                if (forQuarter) {
                    acc.secondMonthDate.push({
                        allDay: true,
                        appName: event.application.name,
                        end: addMonths(monthlyDate, 1),
                        resource: event,
                        start: addMonths(monthlyDate, 1),
                        title: eventTitle(event)
                    });
                    acc.thirdMonthDate.push({
                        allDay: true,
                        appName: event.application.name,
                        end: addMonths(monthlyDate, 2),
                        resource: event,
                        start: addMonths(monthlyDate, 2),
                        title: eventTitle(event)
                    });
                }
            }

            if (
                event.type === 'renew' &&
                dateInTheStartingCalendarMonth.getFullYear() ===
                    day.getFullYear()
            ) {
                if (
                    dateInTheStartingCalendarMonth.getMonth() === day.getMonth()
                )
                    acc.firstMonthDate.push({
                        allDay: true,
                        appName: event.application.name,
                        end: day,
                        resource: event,
                        start: day,
                        title: eventTitle(event)
                    });
                if (forQuarter) {
                    if (
                        addMonths(
                            dateInTheStartingCalendarMonth,
                            1
                        ).getMonth() === day.getMonth()
                    ) {
                        acc.secondMonthDate.push({
                            allDay: true,
                            appName: event.application.name,
                            end: day,
                            resource: event,
                            start: day,
                            title: eventTitle(event)
                        });
                    }

                    if (
                        addMonths(
                            dateInTheStartingCalendarMonth,
                            2
                        ).getMonth() === day.getMonth()
                    ) {
                        acc.thirdMonthDate.push({
                            allDay: true,
                            appName: event.application.name,
                            end: day,
                            resource: event,
                            start: day,
                            title: eventTitle(event)
                        });
                    }
                }
            }
            return acc;
        },
        {
            firstMonthDate: [],
            secondMonthDate: [],
            thirdMonthDate: []
        }
    );

    return eventsMap;
};

const updateEvents = ({ allEvents, forQuarter, range, setEvents }) => {
    const events = getEvents({ allEvents, forQuarter, range });
    setEvents(events);
};

const drillDown = ({
    event,
    events,
    setDrillDownDate,
    setDrillDownEvents,
    setDrilldownVisual
}) => {
    const drillDownEvents = [];
    const drillDownDate = new Date(event);

    // Only process events if they exist and are an array
    if (events && Array.isArray(events)) {
        events.forEach((singleEvent) => {
            const eventDay = new Date(singleEvent.start);
            if (
                eventDay.getMonth() === drillDownDate.getMonth() &&
                eventDay.getFullYear() === drillDownDate.getFullYear() &&
                eventDay.getDate() === drillDownDate.getDate()
            ) {
                drillDownEvents.push(singleEvent.resource);
            }
        });
    }
    setDrillDownDate(drillDownDate);
    setDrillDownEvents(drillDownEvents);
    setDrilldownVisual(true);
};

const display = ({ resource, setCurrentContract, setViewingContract }) => {
    setCurrentContract(resource);
    setViewingContract(true);
};

export const ContractCalendar = ({
    abbreviatedMonthDate,
    allEvents,
    date,
    forQuarter,
    loading,
    toolbar
}) => {
    const { t } = useTranslation();
    const [currentContract, setCurrentContract] = useState({});
    const [drillDownDate, setDrillDownDate] = useState(new Date());
    const [drillDownEvents, setDrillDownEvents] = useState([]);
    const [drilldownVisual, setDrilldownVisual] = useState(false);
    const [viewingContract, setViewingContract] = useState(false);

    const [events, setEvents] = useState([]);

    useEffect(() => {
        if (allEvents?.length > 0) {
            updateEvents({
                allEvents,
                forQuarter,
                range: date,
                setEvents
            });
        }
    }, [allEvents, date, forQuarter, loading]);

    const noEventsToShow =
        !(events?.firstMonthDate?.length > 0) &&
        !(events?.secondtMonthDate?.length > 0) &&
        !(events?.thirdMonthDate?.length > 0);

    if (loading) {
        return (
            <Box data-testid="contractCalendarSkeleton">
                <Skeleton height={850} animation="wave" />;
            </Box>
        );
    }

    return (
        <>
            <Box data-testid="contractCalendar">
                {noEventsToShow && forQuarter ? (
                    <Typography variant="body2">
                        {t('Reports.Contracts.noEventMatch')}
                    </Typography>
                ) : (
                    <>
                        <Box
                            {...(forQuarter
                                ? { className: 'break-after' }
                                : {})}
                            data-testid="firstBigCalendar"
                        >
                            <BigCalendar
                                date={date}
                                display={(event) =>
                                    display({
                                        resource: event.resource,
                                        setCurrentContract,
                                        setViewingContract
                                    })
                                }
                                drillDown={(event) =>
                                    drillDown({
                                        event,
                                        events: events.firstMonthDate,
                                        setDrillDownDate,
                                        setDrillDownEvents,
                                        setDrilldownVisual
                                    })
                                }
                                events={events.firstMonthDate}
                                toolbar={toolbar}
                                updateEvents={(range) =>
                                    updateEvents({
                                        allEvents,
                                        forQuarter,
                                        range: range || date,
                                        setEvents
                                    })
                                }
                            />
                        </Box>
                        {forQuarter && (
                            <>
                                <Box
                                    className="break-after"
                                    data-testid="secondBigCalendar"
                                >
                                    <BigCalendar
                                        date={addMonths(date, 1)}
                                        display={(event) =>
                                            display({
                                                resource: event.resource,
                                                setCurrentContract,
                                                setViewingContract
                                            })
                                        }
                                        drillDown={(event) =>
                                            drillDown({
                                                event,
                                                events: events.secondMonthDate,
                                                setDrillDownDate,
                                                setDrillDownEvents,
                                                setDrilldownVisual
                                            })
                                        }
                                        events={events.secondMonthDate}
                                        toolbar={toolbar}
                                        updateEvents={(range) =>
                                            updateEvents({
                                                allEvents,
                                                forQuarter,
                                                range: range || date,
                                                setEvents
                                            })
                                        }
                                    />
                                </Box>
                                <Box
                                    className="break-after"
                                    data-testid="thirdBigCalendar"
                                >
                                    <BigCalendar
                                        date={addMonths(date, 2)}
                                        display={(event) =>
                                            display({
                                                resource: event.resource,
                                                setCurrentContract,
                                                setViewingContract
                                            })
                                        }
                                        drillDown={(event) =>
                                            drillDown({
                                                event,
                                                events: events.thirdMonthDate,
                                                setDrillDownDate,
                                                setDrillDownEvents,
                                                setDrilldownVisual
                                            })
                                        }
                                        events={events.thirdMonthDate}
                                        toolbar={toolbar}
                                        updateEvents={(range) =>
                                            updateEvents({
                                                allEvents,
                                                forQuarter,
                                                range: range || date,
                                                setEvents
                                            })
                                        }
                                    />
                                </Box>
                            </>
                        )}
                    </>
                )}
            </Box>

            {viewingContract && (
                <AppContractDetailsModal
                    abbreviatedMonthDate={abbreviatedMonthDate}
                    closeModal={() => setViewingContract(false)}
                    currentContract={currentContract}
                    isForReport={forQuarter}
                    viewingContract={viewingContract}
                />
            )}

            {drilldownVisual && (
                <ContractListForSpecificDayModal
                    abbreviatedMonthDate={abbreviatedMonthDate}
                    closeDrilldownVisualModal={() => setDrilldownVisual(false)}
                    closeModal={() => setDrilldownVisual(false)}
                    drillDownDate={drillDownDate}
                    drillDownEvents={drillDownEvents}
                    drilldownVisual={drilldownVisual}
                />
            )}
        </>
    );
};
